import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, Host, Input, OnChanges, signal } from '@angular/core';
import { ResponsiveImageResource, imageMinStyleWidths, imageStyleWidths } from '@app/core/models';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { DrupalConstants } from '@makiwin/ngx-drupal8-rest';

interface Picture {
  media: string;
  srcset?: string;
}

const scaleSize = ['uhd', 'fhd', 'hd', 'md', 'sd'];

/**
 * The `ImageSrcsetComponent` is an Angular component that handles the display of responsive images
 * with support for multiple image sources (`srcset`) and various configurations.
 *
 * This component provides several input properties to customize the behavior and appearance of the image,
 * including CSS classes, image dimensions, alternative text, and error handling.
 *
 * The component also supports calculating the aspect ratio of the image based on the screen width,
 * forcing the use of a specific image size, and displaying a temporary image while the main image is loading.
 *
 * The `ImageSrcsetComponent` interacts directly with the host DOM element and manages the image source set
 * based on the provided responsive image resource.
 *
 * @example
 * ```html
 * <app-image-srcset
 *   [imgClass]="'custom-class'"
 *   [imgId]="'image-id'"
 *   [propertyName]="'property-name'"
 *   [calculateRatio]="true"
 *   [forceOnlyOneSize]="'md'"
 *   [alt]="'Alternative text'"
 *   [onErrorImage]="'/assets/images/error-image.jpeg'"
 *   [tempImg]="'/assets/images/loading-image.jpeg'"
 *   [width]="400"
 *   [height]="300"
 *   [fullPath]="true"
 *   [item]="responsiveImageResource">
 * </app-image-srcset>
 * ```
 *
 * @class
 * @implements {OnChanges}
 */
@Component({
  selector: 'image-srcset',
  standalone: true,
  imports: [CommonModule, PipesModule, NgOptimizedImage],
  templateUrl: './image-srcset.component.html',
  styleUrls: ['./image-srcset.component.scss'],
})
export class ImageSrcsetComponent implements OnChanges {
  /**
   * CSS class to apply to the image element.
   * This allows for custom styling of the image.
   */
  @Input()
  imgClass = '';
  /**
   * The ID of the image to be displayed.
   * This input property is used to specify the unique identifier for the image.
   */
  @Input() imgId = '';
  /**
   * The name of the property to be used.
   * This is an optional input property.
   */
  @Input()
  propertyName?: string;
  /**
   * A boolean input that determines whether the aspect ratio of the image should be calculated,
   * considering the screen width and the image width.
   * If set to `true`, the component will calculate the sources using screen width
   * else it will img width.
   * Default value is `false`.
   */
  @Input()
  calculateRatio = false;
  /**
   * An optional input property that, when provided, forces the component to use only one image size.
   * This can be useful in scenarios where a specific image size is required regardless of the available srcset options.
   *
   * @type {string}
   * @optional
   */
  @Input()
  forceOnlyOneSize?: string;
  /**
   * Alternative text for the image, used for accessibility purposes.
   * This text will be displayed if the image cannot be loaded.
   */
  @Input()
  alt?: string;
  /**
   * The URL of the image to be displayed in case of an error loading the primary image.
   * This input property is optional.
   */
  @Input()
  onErrorImage?: string;
  /**
   * An optional input property that holds the temporary image URL.
   * This can be used to display a placeholder or loading image
   * before the actual image is fully loaded.
   */
  @Input()
  tempImg?: string;
  /**
   * The width of the image in pixels.
   * Default value is 300.
   */
  @Input() width = 300;
  /**
   * The height of the image in pixels.
   * Default value is 300.
   */
  @Input() height = 300;
  /**
   * Indicates whether the full path should be used.
   * If set to `true`, the full path will be used.
   * If set to `false`, a relative path will be used.
   */
  @Input() fullPath = false;
  /**
   * The source URL of the image.
   * This property is optional and can be undefined.
   */
  protected src?: string;
  /**
   * The `srcset` property represents the source set attribute for an image element.
   * It is used to define a list of possible image sources for the browser to choose from,
   * based on the device's screen size, resolution, and other factors.
   *
   * @protected
   * @type {string}
   */
  protected srcset?: string;
  /**
   * A reference to the host DOM element.
   * This is used to interact directly with the element in the component's template.
   */
  protected host: ElementRef;
  /**
   * A protected property that defines the type of the safe resource.
   * The default value is 'url'.
   */
  protected safeType = 'url';
  /**
   * Optional string representing the sizes attribute for the image source set.
   * This attribute specifies the intended display size of the image.
   */
  protected sizes?: string;
  /**
   * An array of Picture objects that represents the set of pictures
   * to be used in the image source set component.
   * This array is protected and can only be accessed within the class
   * or its subclasses.
   */
  protected picturesSet: Picture[] = [];
  /**
   * The largest size of the image in the srcset.
   * This property is optional and may not be defined.
   */
  protected biggestSize?: string;
  /**
   * True if the image is a base64 encoded url.
   */
  protected isBase64 = signal<boolean>(false);
  /**
   * Represents an image item that can be of type `ResponsiveImageResource` or any other type.
   * This property is used to store the responsive image resource or any other type of image data.
   *
   * @private
   * @type {ResponsiveImageResource | any}
   */
  private imageItem?: ResponsiveImageResource | any;

  /**
   * The ratio of the screen, which can be a number or any other type.
   * This property is optional.
   */
  private ratioScreen?: number | any;

  constructor(@Host() hostElement: ElementRef) {
    this.host = hostElement;
  }

  /**
   * Sets the image item and triggers the setItem method.
   *
   * @param item - The responsive image resource to be set.
   */
  @Input() set item(item: ResponsiveImageResource) {
    this.imageItem = item;
    this.setItem();
  }

  ngOnChanges(): void {
    this.setItem();
  }

  /**
   * Handles the image error event by setting a fallback image source.
   *
   * @param e - The error event object.
   * @param path - The fallback image path to be used when an error occurs.
   */
  handleImgError(e: any, path: any) {
    e.target.onerror = null;
    Array.from(e.target.parentNode.children).forEach((element: any) => {
      element.srcset = path;
    });
    e.target.src = path;
  }

  /**
   * Sets the image item and initializes the image source set.
   *
   * This method performs the following actions:
   * - Calculates the screen ratio if `calculateRatio` is true.
   * - Initializes `picturesSet` as an empty array.
   * - If `imageItem` is not provided, sets a default image.
   * - If `imageItem` is a string, sets it as the normal image.
   * - Extracts the property name from `imageItem` keys.
   * - Determines if the image path is a full path.
   * - Sets the image source (`src`) based on the property name and `forceOnlyOneSize`.
   * - Sanitizes the image source if it is a blob URL.
   * - Calls `setBigSize` to set the big size image.
   * - Initializes the image source set if `forceOnlyOneSize` is not specified.
   *
   * @private
   */
  private setItem() {
    if (this.calculateRatio) {
      this.ratioScreen = this.width / window.innerWidth;
      this.ratioScreen = this.ratioScreen === 0 ? 1 : this.ratioScreen;
    } else {
      this.ratioScreen = 1;
    }

    this.picturesSet = [];

    if (!this.imageItem) {
      const tempImg = this.tempImg ?? '/assets/images/default-image.jpeg';
      this.setNormalImage(tempImg);
      return;
    } else if (typeof this.imageItem === 'string') {
      this.setNormalImage(this.imageItem, !this.fullPath);
      return;
    }

    const frags = Object.keys(this.imageItem)
      .filter((key) => key !== 'max_2600x2600')[0]
      .split('_');
    frags.pop();
    if (!this.propertyName) {
      this.propertyName = frags.join('_');
    }
    if (this.propertyName) {
      this.fullPath =
        this.imageItem[`${this.propertyName}_md`] && this.isFullPathImage(this.imageItem[`${this.propertyName}_md`]);
      if (this.fullPath) {
        this.src = this.forceOnlyOneSize
          ? `${this.imageItem[`${this.propertyName}_${this.forceOnlyOneSize}` as keyof ResponsiveImageResource]}`
          : `${this.imageItem[`${this.propertyName}_md` as keyof ResponsiveImageResource]}`;
        // WEB-552 need to sanitize blob image
        this.safeType = this.src.indexOf('blob:') !== -1 ? 'resourceUrl' : 'url';
      } else {
        this.src = this.forceOnlyOneSize
          ? `${DrupalConstants.backEndUrl}${
              this.imageItem[`${this.propertyName}_${this.forceOnlyOneSize}` as keyof ResponsiveImageResource]
            }`
          : `${DrupalConstants.backEndUrl}${
              this.imageItem[`${this.propertyName}_md` as keyof ResponsiveImageResource]
            }`;
      }
    }
    this.setBigSize();
    if (!this.forceOnlyOneSize) {
      this.initSet();
    }
  }

  private initSet(): void {
    // WEB-552 if the src is a blob it's better to use only a src
    // instead due to a problem to sanitize srcst
    if (this.safeType === 'resourceUrl') {
      this.srcset = undefined;
      return;
    }
    // let style = '';
    // const sizes = [];
    let privateImage: any = '';

    Object.keys(imageStyleWidths).forEach((styleWidthName) => {
      if (styleWidthName.indexOf(this.propertyName as any) === -1) {
        return;
      }

      if (!this.imageItem[styleWidthName as keyof ResponsiveImageResource]) {
        return;
      }

      // if (!this.fullPath) {
      //   style += DrupalConstants.backEndUrl;
      // }
      // const ratioCalc = Math.round((styleValue * devicePixelRatio) / this.ratioScreen);
      // style += `${this.imageItem[styleWidthName]} ${styleValue}w,`;
      const pre = imageMinStyleWidths[styleWidthName]
        ? `(min-width: ${imageMinStyleWidths[styleWidthName] / this.ratioScreen}px) and `
        : '';
      // let size = `(max-width: ${styleValue}px) ${styleValue}px`;
      let url = this.imageItem[styleWidthName as keyof ResponsiveImageResource];
      const styleValue = Math.round(imageStyleWidths[styleWidthName] / this.ratioScreen);
      if (!this.fullPath) {
        url = DrupalConstants.backEndUrl + url;
      }
      this.picturesSet.push({
        media:
          this.biggestSize === styleWidthName && pre ? pre.replace('and', '') : `${pre} (max-width: ${styleValue}px)`,
        srcset: url,
      });
      // if (pre !== '') {
      //   size = `(${pre} (max-width: ${styleValue}px)) ${styleValue}px`;
      // }

      // sizes.push(size);
      if (imageStyleWidths[styleWidthName] <= window.innerWidth) {
        privateImage = this.imageItem[styleWidthName as keyof ResponsiveImageResource];
        if (!this.fullPath) {
          privateImage += DrupalConstants.backEndUrl;
        }
      }
    });

    if (privateImage && this.calculateRatio) {
      this.setNormalImage(privateImage);
    }
    // else {
    // this.sizes = sizes.join(',');
    // this.srcset = style.slice(0, -1);
    // }
  }

  /**
   * Check if the image is a full path (domain included or not)
   * or a base64 encoded url.
   * @param  {string} image
   * @returns
   */
  private isFullPathImage(image: string) {
    this.isBase64.set(image.indexOf('data:image') !== -1);
    return image.indexOf('http') !== -1 || image.indexOf('data:image') !== -1 || image.indexOf('/assets') === 0;
  }

  private setNormalImage(url: string, addBackEndUrl = false): void {
    if (this.isFullPathImage(url)) {
      this.src = url;
    } else {
      this.src = addBackEndUrl ? DrupalConstants.backEndUrl + url : url;
    }
    // this.srcset = url;
  }

  /**
   * Sets the `biggestSize` property to the largest available size of the image.
   * Iterates through the `scaleSize` array and checks if the `imageItem` has a property
   * corresponding to the combination of `propertyName` and the current size.
   * If such a property is found, it sets `biggestSize` to that property name and exits the function.
   * If `imageItem` is not defined, the function returns immediately.
   *
   * @private
   */
  private setBigSize() {
    if (!this.imageItem) {
      return;
    }
    for (const size of scaleSize) {
      if (!this.imageItem[(this.propertyName + '_' + size) as keyof ResponsiveImageResource]) {
        continue;
      }
      this.biggestSize = this.propertyName + '_' + size;
      return;
    }
  }
}
